'use client';

import { CheckboxIcon } from '@/components/icons/checkbox-icon';
import { ChevronDownIcon } from '@/components/icons/chevron-down-icon';
import { ChevronUpIcon } from '@/components/icons/chevron-up-icon';
import { cn } from '@/lib/utils';
import * as SelectPrimitive from '@radix-ui/react-select';
import * as React from 'react';

const Select = ({
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Root>): React.JSX.Element => {
  return <SelectPrimitive.Root data-slot="select"
    {...props}
  />;
};

const SelectGroup = ({
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Group>): React.JSX.Element => {
  return <SelectPrimitive.Group data-slot="select-group"
    {...props}
  />;
};

const SelectValue = ({
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Value>): React.JSX.Element => {
  return <SelectPrimitive.Value data-slot="select-value"
    {...props}
  />;
};

const SelectTrigger = ({
  children,
  className,
  size = 'default',
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Trigger> & {
  size?: 'sm' | 'default';
}): React.JSX.Element => {
  return (
    <SelectPrimitive.Trigger className={cn(
      "border-input data-[placeholder]:text-muted-foreground [&_svg:not([class*='text-'])]:text-muted-foreground",
      'focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-2 aria-invalid:ring-primary',
      'aria-invalid:border-primary dark:bg-input/30 dark:hover:bg-input/50 flex w-fit items-center justify-between gap-2',
      'rounded-md border bg-white px-3 py-2 text-sm whitespace-nowrap shadow-xs transition-[color,box-shadow] outline-none',
      'focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50 data-[size=default]:h-9 data-[size=sm]:h-8',
      '*:data-[slot=select-value]:line-clamp-1 *:data-[slot=select-value]:flex *:data-[slot=select-value]:items-center',
      "*:data-[slot=select-value]:gap-2 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
      className
    )}
    data-size={size}
    data-slot="select-trigger"
    {...props}
    >
      {children}
      <SelectPrimitive.Icon asChild>
        <ChevronDownIcon className="size-4 opacity-50" />
      </SelectPrimitive.Icon>
    </SelectPrimitive.Trigger>
  );
};

const SelectContent = ({
  children,
  className,
  position = 'popper',
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Content>): React.JSX.Element => {
  return (
    <SelectPrimitive.Portal>
      <SelectPrimitive.Content className={cn(
        'bg-popover text-popover-foreground data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0',
        'data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2',
        'data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 relative z-50',
        'max-h-(--radix-select-content-available-height) min-w-[8rem] origin-(--radix-select-content-transform-origin) overflow-x-hidden',
        'overflow-y-auto rounded-md border shadow-md',
        position === 'popper' &&
            'data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1',
        className
      )}
      data-slot="select-content"
      position={position}
      {...props}
      >
        <SelectScrollUpButton />
        <SelectPrimitive.Viewport className={cn(
          'p-1',
          position === 'popper' &&
              'h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)] scroll-my-1'
        )}
        >
          {children}
        </SelectPrimitive.Viewport>
        <SelectScrollDownButton />
      </SelectPrimitive.Content>
    </SelectPrimitive.Portal>
  );
};

const SelectLabel = ({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Label>): React.JSX.Element => {
  return (
    <SelectPrimitive.Label className={cn('text-muted-foreground px-2 py-1.5 text-xs', className)}
      data-slot="select-label"
      {...props}
    />
  );
};

const SelectItem = ({
  children,
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Item>): React.JSX.Element => {
  return (
    <SelectPrimitive.Item className={cn(
      "focus:bg-accent focus:text-accent-foreground [&_svg:not([class*='text-'])]:text-muted-foreground relative flex w-full",
      'cursor-default items-center gap-2 rounded-sm py-1.5 pr-8 pl-2 text-sm outline-hidden select-none data-[disabled]:pointer-events-none',
      "data-[disabled]:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4 *:[span]:last:flex",
      '*:[span]:last:items-center *:[span]:last:gap-2',
      className
    )}
    data-slot="select-item"
    {...props}
    >
      <span className="absolute right-2 flex size-3.5 items-center justify-center">
        <SelectPrimitive.ItemIndicator>
          <CheckboxIcon className="size-4" />
        </SelectPrimitive.ItemIndicator>
      </span>
      <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
    </SelectPrimitive.Item>
  );
};

const SelectSeparator = ({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Separator>): React.JSX.Element => {
  return (
    <SelectPrimitive.Separator className={cn('bg-border pointer-events-none -mx-1 my-1 h-px', className)}
      data-slot="select-separator"
      {...props}
    />
  );
};

const SelectScrollUpButton = ({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.ScrollUpButton>): React.JSX.Element => {
  return (
    <SelectPrimitive.ScrollUpButton className={cn(
      'flex cursor-default items-center justify-center py-1',
      className
    )}
    data-slot="select-scroll-up-button"
    {...props}
    >
      <ChevronUpIcon className="size-4" />
    </SelectPrimitive.ScrollUpButton>
  );
};

const SelectScrollDownButton = ({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.ScrollDownButton>): React.JSX.Element => {
  return (
    <SelectPrimitive.ScrollDownButton className={cn(
      'flex cursor-default items-center justify-center py-1',
      className
    )}
    data-slot="select-scroll-down-button"
    {...props}
    >
      <ChevronDownIcon className="size-4" />
    </SelectPrimitive.ScrollDownButton>
  );
};

export {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectScrollDownButton,
  SelectScrollUpButton,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
};
